import type { DataStore, EventBus, HostApi, RowSourceCtx, Table } from '@easydb/shared';
import { createRoutedDataStore, withUniqueTableNames } from './db/index.js';
import { createIpcDataStore } from './db/data-store-bridge.js';
import { activeEdbName, startEdbSession, type EdbSession } from './db/edb/session.js';
import { adoptFolderFile, adoptLocalDb, leaveFileForIndex, noteSessionOpened, planForMissingSpace } from './db/edb/space-adopt.js';
import { mayCreateWorkspaceIn, slugifyWorkspace } from './db/edb/space-resolve.js';
import { recordOpenWorkspaces } from './db/edb/space-registry.js';
import { openDatabaseName } from './db/file-workspaces.js';
import { showStorageFailure } from './chrome/storage-failure.js';
import { createEventBus } from './events/bus.js';
import { createRegistries, type Registries } from './plugin-host/registries.js';
import { createHostApi } from './plugin-host/api-factory.js';
import { loadBuiltinPlugins } from './plugin-host/loader.js';
import { registerCoreCommands } from './plugin-host/core-commands.js';
import { loadUrlPlugins } from './plugin-host/url-loader.js';
import { SAFE_MODE } from './plugin-host/safe-mode.js';
import { startFileLinkGuard } from './util/file-link-guard.js';
import { startLinkPolicy } from './util/link-settings.js';
import { startFilterDefaults } from './util/filter-settings.js';
import { startWindowColors } from './window-mgr/window-color-settings.js';

export interface AppContext {
  store: DataStore;
  events: EventBus;
  workspaceId: string;
  registries: Registries;
  api: HostApi;
}

let ctxPromise: Promise<AppContext> | null = null;

/**
 * Start the browser's SQLite session, or put a blocking notice on screen.
 *
 * There is no second store to fall back to, so a failure here is fatal by
 * design. The notice goes up BEFORE the rejection propagates, because the
 * rejection alone would leave a blank page — `getContext()` is awaited by every
 * component and nothing else is watching for it.
 */
async function startSessionOrExplain(): Promise<EdbSession> {
  try {
    const session = await startEdbSession();
    // The database this boot really opened. An adopt-attempt marker naming it has
    // done its job — see `noteSessionOpened`.
    noteSessionOpened(session.name);
    return session;
  } catch (err) {
    showStorageFailure(err instanceof Error ? err.message : String(err));
    throw err;
  }
}

export function getContext(): Promise<AppContext> {
  if (!ctxPromise) ctxPromise = init();
  return ctxPromise;
}

async function init(): Promise<AppContext> {
  // Settings are workspace-scoped, but the active workspace is resolved further
  // down using this very store — so the store reads the id through this holder,
  // which is filled before any settings access happens.
  let activeWorkspaceId = '';
  // SQLite everywhere, over one adapter. `EdbBridge` implements the same
  // `EasydbStoreBridge` the Electron preload does, so a worker running
  // sqlite-wasm and a preload talking to the main process are the same shape to
  // everything downstream — routing, workspace resolution and the plugin host
  // all see one store.
  //
  // The two differ only in WHERE the database lives: a file the desktop opened,
  // or this tab's own SQLite database (`INDEX_DB_NAME`, or a `.edb` the user
  // adopted). Persistence is a separate concern from being in SQL mode — the
  // database is live and queryable whether or not anything has been written to
  // disk yet.
  const baseStore = window.easydb?.store ? createIpcDataStore(window.easydb.store, () => activeWorkspaceId) : createIpcDataStore((await startSessionOrExplain()).bridge, () => activeWorkspaceId);
  const events = createEventBus();
  const registries = createRegistries();

  // Row-source routing (Phase 2a): tables that declare a `source` are backed
  // by a registered provider instead of the local database. `rows(tableId)` needs the
  // Table synchronously, so keep a cache primed from a live subscription. A
  // cache miss falls through to the local path, so local tables — and the
  // window before the cache warms — behave exactly as before.
  const tableCache = new Map<string, Table>();
  baseStore.tables.subscribe((all) => {
    tableCache.clear();
    for (const t of all) tableCache.set(t.id, t);
  });

  // Seed the cache the instant a table is inserted/updated — the caller's
  // `await` returns with it already cached, without waiting for the async
  // subscription above. A freshly-connected live
  // table (source-backed) must be routable the moment its grid panel reads
  // `rows(id)`; otherwise the panel binds to the empty *local* collection and
  // shows its columns but no rows until the next subscription tick (the
  // "Connect shows no rows" bug — Import is unaffected as it has no `source`).
  //
  // Both hops go through `withUniqueTableNames`, which is what stops any writer
  // from creating a second table under a name this workspace already uses. The
  // cache is seeded from what the guard actually STORED (a uniqued name), not
  // from what the caller asked for.
  const uniqueTables = withUniqueTableNames(baseStore.tables);
  const cachingTables: typeof baseStore.tables = {
    ...uniqueTables,
    insert: async (doc) => {
      const stored = await uniqueTables.insert(doc);
      tableCache.set(stored.id, stored);
      return stored;
    },
    upsert: async (doc) => {
      const stored = await uniqueTables.upsert(doc);
      tableCache.set(stored.id, stored);
      return stored;
    },
  };

  // Providers created lazily at `rows()` time need `backend.fetch`, which is
  // built on the HostApi below. Expose it through a getter so the ctx can be
  // constructed before the api without a second backend implementation.
  let apiRef: HostApi | null = null;
  const rowSourceCtx: RowSourceCtx = {
    get backend() {
      if (!apiRef) throw new Error('[host] row-source ctx used before app init completed');
      return apiRef.backend;
    },
    events,
    settings: baseStore.settings,
    workspaceId: () => workspaceId,
  };

  const store = createRoutedDataStore({
    base: { ...baseStore, tables: cachingTables },
    providers: registries.rowSources,
    tableById: (id) => tableCache.get(id),
    ctx: rowSourceCtx,
  });

  // Workspace resolution priority:
  //   1. ?space=NAME URL param — if a matching workspace exists, use it.
  //                              If not, create one with that id+name.
  //   2. The last-active workspace (persisted device-local) if it still exists.
  //      This is what makes opening the app in a NEW TAB (a bare URL with no
  //      ?space=) show the workspace you were last using — otherwise it fell
  //      back to the first workspace, which reads as "my data is gone".
  //   3. Otherwise, the first workspace in the store.
  //   4. Otherwise, create a "default" workspace.
  const requested = readWorkspaceFromUrl();
  const existing = await store.workspaces.find();
  let workspaceId: string;
  if (requested) {
    const id = slugifyWorkspace(requested);
    const hit = existing.find((w) => w.id === id);
    if (hit) {
      workspaceId = hit.id;
    } else {
      // Not here — but "not in the database this tab happens to have open" is not
      // the same as "does not exist". `<id>.edb` may be a database this browser
      // already holds, or a file sitting in the user's workspace folder, and a
      // link to a workspace has to be able to find it. Anything but `create`
      // reloads and never comes back. See `db/edb/space-resolve.ts`.
      const action = await planForMissingSpace(id);
      if (action === 'adopt-local-db') await adoptLocalDb(id);
      // These two fall through when what the probe saw has since gone.
      if (action === 'adopt-folder-file') await adoptFolderFile(id);
      // The one line that creates a workspace at boot, so the one place to enforce
      // that a `.edb` holds only the workspace its name says. Every route above can
      // arrive here — 'create', a folder that has to be asked for, or an adopt whose
      // target has since gone — and three of them used to create the workspace
      // inside whichever file the tab had open. Never returns: it leaves the file
      // for the project index and reloads, and that boot creates it there.
      if (!mayCreateWorkspaceIn(activeEdbName(), id)) await leaveFileForIndex(id);
      const created = await store.workspaces.insert({
        id,
        // What was ASKED for becomes the title, where it is not simply the id
        // spelled out. `?space=Power Plants` means the workspace `power-plants`,
        // and the words the user wrote are worth keeping as what it is called.
        ...(requested === id ? {} : { title: requested }),
        createdAt: Date.now(),
        pluginUrls: [],
      });
      workspaceId = created.id;
    }
  } else {
    const last = readLastWorkspace();
    const lastHit = last ? existing.find((w) => w.id === last) : undefined;
    if (lastHit) {
      workspaceId = lastHit.id;
    } else if (existing.length > 0) {
      workspaceId = existing[0]!.id;
    } else {
      // The SECOND line that creates a workspace at boot, and it needs the same
      // guard as the one above. Deleting the last workspace of an adopted
      // `alpha.edb` lands here with no `?space=` (`openResolvedWorkspace`), and
      // creating `default` inside that file is exactly what the rule forbids —
      // the store would throw, `init()` would reject, and the tab would come back
      // to the same blocking notice on every reload, with the file marker still
      // pointing at `alpha.edb`.
      if (!mayCreateWorkspaceIn(activeEdbName(), 'default')) await leaveFileForIndex('default');
      const ws = await store.workspaces.insert({
        id: 'default',
        createdAt: Date.now(),
        pluginUrls: [],
      });
      workspaceId = ws.id;
    }
  }

  // Point the settings view at the resolved workspace before anything reads a
  // setting (the plugin host below does, immediately).
  activeWorkspaceId = workspaceId;

  // Remember the active workspace so a fresh tab / reload without ?space= comes
  // back to it (see resolution step 2 above).
  persistLastWorkspace(workspaceId);

  // And remember what the PROJECT INDEX holds, so the list survives this tab
  // adopting a `.edb` — from inside one, nothing can open the index to ask. A
  // read, not a write to the store: boot must not mark the workspace unsaved. It
  // re-reads rather than reusing `existing`, which predates the workspace the two
  // branches above may just have created. See `db/edb/space-registry.ts`.
  recordOpenWorkspaces(openDatabaseName(), await store.workspaces.find());

  const api = createHostApi({
    store,
    events,
    registries,
    workspaceId: () => workspaceId,
  });
  apiRef = api;

  // Core (non-plugin) commands for the Ctrl+K palette — registered before
  // plugins so the window-management commands are always present.
  registerCoreCommands(api);

  // Which protocols may be links is a setting, and every renderer that draws one
  // reads it while painting — so it is resolved here, once, and re-resolved when
  // it changes. See `util/link-settings.ts`.
  startLinkPolicy(api.settings);
  startFilterDefaults(api.settings);

  // Which colours a window's palette button offers is a setting too, and the
  // picker is built inside a click handler. Same shape, same reason — see
  // `window-mgr/window-color-settings.ts`.
  startWindowColors(api.settings);

  // A `file:///` link cannot be opened by a browser tab, and the browser says so
  // by opening a blank one. Catch the click and put the path on the clipboard
  // instead. Stands aside in the desktop build, whose page IS `file:`.
  startFileLinkGuard((message) => api.ui.dialogs.toast(message, { kind: 'info', title: 'Local file' }));

  // Centralized import-status toasts so every importer (csv, json, gist pull,
  // future ones) gets consistent UX without duplicating the toast call.
  // datasette-source is the exception: it emits its own batch-aware summary
  // (one toast for a whole multi-table import), so we skip the generic
  // per-table toast here to avoid two messages for a single import.
  events.on('import:after', ({ source, tableId, rowCount }) => {
    if (source === 'datasette') return;
    void api.store.tables.findOne(tableId).then((t) => {
      api.ui.dialogs.toast(`Imported ${rowCount} row${rowCount === 1 ? '' : 's'} into "${t?.name ?? tableId}".`, { kind: 'success', title: source.toUpperCase() + ' import' });
    });
  });
  events.on('plugin:error', ({ url, phase, error }) => {
    api.ui.dialogs.toast(`[${phase}] ${(error as Error)?.message ?? String(error)}`, {
      kind: 'error',
      title: `Plugin: ${url}`,
    });
  });

  // init() built-ins synchronously, then trigger load() after we emit app:ready.
  // URL plugins are loaded after built-ins so the latter establish the host
  // surface (default cell renderers, importers, etc.) before user code runs.
  const runLoadBuiltins = await loadBuiltinPlugins(api);
  const runLoadUrls = await loadUrlPlugins(api);

  queueMicrotask(async () => {
    events.emit('app:ready', { workspaceId });
    await runLoadBuiltins();
    await runLoadUrls();

    // Safe mode is transient (see plugin-host/safe-mode.ts) — it never
    // touches persisted state, so the user could otherwise have no idea why
    // plugins/features are missing. Fire the warning after the load passes
    // above so `toast-host` (mounted in app-shell's initial render, long
    // before this microtask runs) is definitely ready.
    if (SAFE_MODE === 'all-optional') {
      api.ui.dialogs.toast(
        'Safe mode is ON: only fixed built-ins (Settings, core rendering) loaded. ' +
          'All other built-in plugins and URL-installed plugins are disabled for this ' +
          'session only — nothing was changed. Disable the culprit below, then reload ' +
          'without ?safemode.',
        { kind: 'warning', title: 'Safe mode' },
      );
    } else if (SAFE_MODE === 'url-plugins') {
      api.ui.dialogs.toast('Safe mode (URL plugins) is ON: URL-installed plugins were not loaded this ' + 'session. Built-in plugins are unaffected. Reload without ?safemode1 to restore them.', {
        kind: 'warning',
        title: 'Safe mode',
      });
    }

    // Safe mode exists to reach the Plugin Manager when a plugin breaks the
    // app, so open it for the user instead of making them find the button in a
    // half-loaded shell. Both levels get it — the dialog marks the plugins each
    // level skipped and is where the user turns the culprit off for good.
    if (SAFE_MODE !== 'off') api.ui.openPluginManager();
  });

  // User scripting: the very HostApi that plugins receive, on `window.api`, so
  // anything a plugin can do can also be typed into the browser console —
  // `api.store.tables.find()`, `api.ui.dialogs.toast('hi')`. Not gated by
  // `?test=1` (that gate is for `window.__easydb`, the whole AppContext).
  (globalThis as unknown as Record<string, unknown>).api = api;

  return { store, events, workspaceId, registries, api };
}

/**
 * The workspace the URL asks for.
 *
 * `?space=` is the spelling every link in this app writes. `?workspace=` is
 * accepted because it is what people type from memory, and a parameter that is
 * silently ignored looks like the workspace itself failed to load.
 */
function readWorkspaceFromUrl(): string | null {
  if (typeof location === 'undefined') return null;
  const sp = new URLSearchParams(location.search);
  const v = sp.get('space') ?? sp.get('workspace');
  return v && v.trim().length > 0 ? v.trim() : null;
}

/** Device-local key holding the id of the workspace last opened on this origin. */
const LAST_WORKSPACE_KEY = 'eda:lastWorkspaceId';

function readLastWorkspace(): string | null {
  try {
    return globalThis.localStorage?.getItem(LAST_WORKSPACE_KEY) ?? null;
  } catch {
    return null; // localStorage can throw (private mode / disabled) — ignore.
  }
}

/**
 * Drop the remembered workspace if it is `id`. Called when a workspace is
 * deleted: otherwise the next reload without `?space=` resolves step 2 to a
 * workspace that no longer exists, and lands on whichever one happens to be
 * first instead of the one the user was sent to.
 */
export function forgetLastWorkspace(id: string): void {
  try {
    if (globalThis.localStorage?.getItem(LAST_WORKSPACE_KEY) === id) {
      globalThis.localStorage.removeItem(LAST_WORKSPACE_KEY);
    }
  } catch {
    /* ignore — persistence is best-effort */
  }
}

function persistLastWorkspace(id: string): void {
  try {
    globalThis.localStorage?.setItem(LAST_WORKSPACE_KEY, id);
  } catch {
    /* ignore — persistence is best-effort */
  }
}

// `slugifyWorkspace` moved to `db/edb/space-resolve.ts`, next to the file-name
// rules it has to agree with. Re-exported because it is part of this module's
// published surface — `chrome/` and the plugins import it from here.
export { slugifyWorkspace } from './db/edb/space-resolve.js';
